<template>
  <auth-layout>
    <div class="form">
      <div class="form__body">
        <!-- логотип показываем только на экране успешной регистрации:
             в форме он убран, чтобы форма целиком умещалась на экране без прокрутки -->
        <header v-if="isRegistered" class="header">
          <SvgBase width="129" height="35">
            <SvgLogo />
          </SvgBase>
        </header>

        <template v-if="isRegistered">
          <h1 class="text-h6 text-center">Регистрация завершена</h1>
          <p class="text-body-1 text-grey text-center">
            На указанный адрес электронной почты отправлено письмо со ссылкой для завершения
            регистрации: подтверждения e-mail и создания пароля.
          </p>
          <p class="text-body-2 text-grey text-center">
            Если письмо не пришло, проверьте папку «Спам».
          </p>
          <v-btn :block="true" class="registration-submit" color="primary" to="/login">
            Перейти к входу
          </v-btn>
        </template>

        <!-- Vuetify 3.1.14: validate-on принимает только ОДНО значение ('input' | 'blur' | 'submit'),
             комбинации не работают. 'blur' — проверка при уходе фокуса с поля -->
        <v-form
          v-else
          ref="registrationForm"
          v-model="isFormValid"
          class="registration-form"
          validate-on="blur"
          @submit.prevent="submit"
        >
          <div class="form-section">
            <div class="form-section__header">
              <i class="pi pi-user form-section__icon" />
              <span class="form-section__title">Личные данные</span>
            </div>

            <v-text-field
              v-model="form.lastname"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="50"
              :rules="[rules.required]"
            >
              <template #label>Фамилия<span class="required-asterisk">*</span></template>
            </v-text-field>

            <v-text-field
              v-model="form.firstname"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="50"
              :rules="[rules.required]"
            >
              <template #label>Имя<span class="required-asterisk">*</span></template>
            </v-text-field>

            <v-text-field
              v-model="form.middlename"
              label="Отчество"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="50"
            />

            <v-radio-group
              v-model="form.gender"
              inline
              density="compact"
              hide-details="auto"
              class="gender-group"
              validate-on="input"
              :rules="[rules.required]"
            >
              <span class="gender-group__label"> Пол<span class="required-asterisk">*</span> </span>
              <v-radio label="Мужской" value="male" />
              <v-radio label="Женский" value="female" />
            </v-radio-group>

            <v-text-field
              v-model="form.birthDate"
              label="Дата рождения"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="10"
              inputmode="numeric"
              placeholder="дд.мм.гггг"
              :rules="[birthDateRule]"
            />
          </div>

          <div class="form-section">
            <div class="form-section__header">
              <i class="pi pi-id-card form-section__icon" />
              <span class="form-section__title">Контакты</span>
            </div>

            <v-text-field
              v-model="form.email"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="100"
              :error-messages="emailServerError"
              :rules="[rules.required, rules.email]"
              @update:model-value="emailServerError = ''"
            >
              <template #label>E-mail<span class="required-asterisk">*</span></template>
            </v-text-field>

            <v-text-field
              v-model="form.phone"
              v-mask="'+7 (###) ###-##-##'"
              label="Телефон"
              variant="outlined"
              density="compact"
              hide-details="auto"
              inputmode="numeric"
              :rules="[phoneRule]"
            />
          </div>

          <div class="form-section">
            <div class="form-section__header">
              <i class="pi pi-map-marker form-section__icon" />
              <span class="form-section__title">Адрес</span>
            </div>

            <v-text-field
              v-model="form.region"
              label="Регион"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="50"
            />

            <v-text-field
              v-model="form.city"
              label="Населённый пункт"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="50"
            />
          </div>

          <div class="form-section">
            <div class="form-section__header">
              <i class="pi pi-briefcase form-section__icon" />
              <span class="form-section__title">Работа</span>
            </div>

            <v-text-field
              v-model="form.organization"
              label="Организация"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="100"
            />

            <v-text-field
              v-model="form.position"
              label="Должность"
              variant="outlined"
              density="compact"
              hide-details="auto"
              maxlength="100"
            />
          </div>

          <v-checkbox
            v-model="form.agreement"
            color="primary"
            hide-details="auto"
            validate-on="input"
            :rules="[(v) => v === true || 'Необходимо подтвердить согласие']"
          >
            <template #label>
              <span class="text-body-2">
                Я даю согласие на обработку моих персональных данных в соответствии с
                <a
                  href="/Пользовательское соглашение.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Пользовательским соглашением
                </a>
                и
                <a
                  href="/Политика конфиденциальности.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Политикой конфиденциальности
                </a>
              </span>
            </template>
          </v-checkbox>

          <v-btn
            :block="true"
            class="registration-submit"
            color="primary"
            type="submit"
            :loading="isLoading"
            :disabled="isLoading || isFormValid === false"
          >
            Зарегистрироваться
          </v-btn>

          <p class="text-body-1 text-grey text-center auth-hint">
            Уже есть учётная запись?
            <router-link to="/login">Войти</router-link>
          </p>
        </v-form>
      </div>
    </div>
  </auth-layout>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { api } from '@/api/Api'
import AuthLayout from '@/layouts/auth'
import SvgBase from '@/components/Svg/SvgBase'
import SvgLogo from '@/components/Svg/SvgLogo.vue'
import { commonValidationRules } from '@/utils/common'
import { useToast } from '@/composables/useToast'

const { toast } = useToast()

const rules = commonValidationRules

const phoneRule = (v) => {
  const digits = String(v ?? '').replace(/\D/g, '')

  return !digits || digits.length === 11 || 'Введите номер телефона полностью'
}

const birthDateRule = (v) => {
  if (!v) return true

  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(v)) return 'Введите дату в формате дд.мм.гггг'

  const [day, month, year] = v.split('.').map(Number)
  const date = new Date(year, month - 1, day)

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'Такой даты не существует'
  }

  if (date > new Date()) return 'Дата не может быть в будущем'

  return true
}

// дд.мм.гггг -> YYYY-MM-DD (формат, который принимает API)
const toIsoBirthDate = (v) => {
  if (!v) return null

  const [day, month, year] = v.split('.')

  return `${year}-${month}-${day}`
}

const form = reactive({
  lastname: '',
  firstname: '',
  middlename: '',
  gender: null,
  birthDate: '',
  email: '',
  phone: '',
  region: '',
  city: '',
  organization: '',
  position: '',
  agreement: false,
})

const isLoading = ref(false)
const isRegistered = ref(false)
// валидность формы: v-form обновляет её при каждой перепроверке полей.
// Кнопка «Зарегистрироваться» активна, пока ошибок нет (или все исправлены)
const isFormValid = ref(true)
// Важно: '' вместо null — Vuetify 3.1.x падает на :error-messages="null"
// (обращение к .length у null внутри useValidation)
const emailServerError = ref('')

const registrationForm = ref(null)

const submit = async () => {
  const { valid } = await registrationForm.value.validate()

  if (!valid) {
    return
  }

  emailServerError.value = ''
  isLoading.value = true

  try {
    await api.user().registerUser({
      lastname: form.lastname,
      firstname: form.firstname,
      middlename: form.middlename || null,
      gender: form.gender,
      birthDate: toIsoBirthDate(form.birthDate),
      email: form.email,
      phone: form.phone || null,
      region: form.region || null,
      city: form.city || null,
      organization: form.organization || null,
      position: form.position || null,
      agreement: form.agreement === true,
    })

    isRegistered.value = true
  } catch (err) {
    const status = err?.response?.status

    if (status === 409) {
      emailServerError.value = 'Пользователь с таким e-mail уже зарегистрирован'
      return
    }

    if (status === 400 && err?.response?.data?.error) {
      toast.error(err.response.data.error)
      return
    }

    toast.error('Не удалось завершить регистрацию. Попробуйте позже.')
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.form {
  width: 550px;
  display: flex;
  flex-direction: column;
  max-width: 1080px;
  justify-content: flex-start;
  align-items: flex-start;

  &__body {
    display: flex;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
    align-items: center;
    justify-content: flex-start;
    padding: 40px 60px;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    gap: 14px;
    width: 100%;
    background: white;

    // безопасное центрирование: когда контент выше карточки,
    // верх не обрезается, а появляется прокрутка
    > :first-child {
      margin-top: auto;
    }

    > :last-child {
      margin-bottom: auto;
    }

    @media (max-width: 599.95px) {
      padding: 24px 16px 45px;
    }
  }

  @media (max-width: 599.95px) {
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
  }
}

.registration-form {
  width: 100%;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  // смысловые блоки формы: «Личные данные», «Контакты», «Адрес», «Работа»
  .form-section {
    display: flex;
    flex-direction: column;
    gap: 10px;

    & + .form-section {
      margin-top: 6px; // увеличенный зазор между блоками (поверх gap формы)
    }
  }

  .form-section__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
  }

  .form-section__icon {
    font-size: 15px;
    line-height: 1;
    color: var(--primary);
  }

  .form-section__title {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-caption);
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  :deep(.v-field__input) {
    font-size: 17px;
  }

  :deep(.v-field-label) {
    font-size: 15px;
  }

  // всплывающий (поднявшийся) label — на ~15% меньше обычного
  :deep(.v-field-label--floating) {
    font-size: 13px;
  }

  // сообщения валидации («Заполните поле» и т.п.) — размером с всплывший label
  // !important обязателен: глобальное правило из ProjectForm.vue (незаскопленный <style>)
  // ставит .v-input--error .v-messages div { font-size: 10px } на всё приложение
  :deep(.v-messages) {
    font-size: 13px !important;
  }

  :deep(.v-messages__message) {
    font-size: 13px !important;
    line-height: 16px;
  }

  .auth-hint a {
    color: var(--primary);
    font-size: inherit;
    text-decoration: underline;
  }
}

.registration-submit {
  // Vuetify: .v-btn--block задаёт flex: 1 0 auto — в flex-колонке .form__body
  // кнопка «Перейти к входу» растягивалась на всю свободную высоту карточки.
  // flex: none запрещает расти; высоту фиксировали и раньше, но селектор
  // button {} не действовал на <a> (v-btn с to рендерится ссылкой, не кнопкой)
  flex: none;
  height: 52px;
  max-height: 56px;
  margin-top: 12px;
  font-size: 18px;
  border-radius: 20px;
  text-transform: uppercase;
}

.required-asterisk {
  color: var(--error);
  margin-left: 4px;
}

.gender-group {
  :deep(.v-selection-control-group) {
    align-items: center;
    width: 100%;
    // Vuetify по умолчанию даёт 16px отступов сверху/снизу — убираем,
    // высоту рамки выравниваем с outlined-полями (32 + 2*7 + 2 = 48px)
    margin: 0;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    padding: 7px 16px;
    column-gap: 16px;
  }

  :deep(.v-selection-control) {
    min-height: 32px;
    cursor: pointer;

    // зазор между кружком и подписью делаем частью <label>:
    // margin не кликабелен, padding — кликабелен. Вся зона «кружок + пол»
    // становится единым кликабельным блоком, визуальная раскладка не меняется
    .v-label {
      margin-left: 0;
      padding-left: 12px;
    }
  }

  &.v-input--error {
    :deep(.v-selection-control-group) {
      border-color: var(--error);
    }
  }
}

.gender-group__label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 15px;
}
</style>
